
import PanelHeader from "@/components/Reusable/Util/PanelHeader.vue";
import { Vue, Component, Ref } from "vue-property-decorator";
import { documentStore, uiStore } from "@/store/store";
import {
  getNodeIdAtPointInStructurePanel,
  getMouseDragStatus,
  initialMouseDragStatus,
  DragPayload
} from "@/lib/structure-panel-drag/util";

import { getNewNodePosition } from "@/lib/structure-panel-drag/get-new-node-position";
import { findNode, getNodeIndex, getNodeParent } from "@/lib/tree/tree";
import { ROOT_ELEMENT_ID } from "@/lib/tree/tree";
import {
  expandOrCollapseAllNodes,
  expandNodesToShowActiveElement,
  structurePanelNodeIsExpanded
} from "@/lib/structure-panel/expand-collapse";
import defensiveCopy from "@/util/defensive-copy";
import {
  closeUiPanel,
  scrollToHtmlElementInsidePanel,
  toggleUiPanel
} from "@/lib/panels/panels";
import { EasyEvent, isCanvasEvent } from "@/util/easy-event";
import { MutationPayload } from "vuex";
import UIModule from "@/store/modules/ui";
import { breakdanceUndo } from "@/lib/undo/undo";
import { getBuilderElementNameBySlug } from "@/lib/undo/snapshot-labels/document-store-property-change.utils";
import StructureContextMenu from "@/components/Structure/StructureContextMenu.vue";
import StandardButton from "@/components/UI/StandardButton.vue";

@Component({
  components: {
    StructureContextMenu,
    PanelHeader,
    StandardButton
  }
})
export default class StructurePanel extends Vue {
  @Ref() readonly panelHeader!: PanelHeader;

  mouseDragStatus = {
    ...initialMouseDragStatus()
  };

  close() {
    closeUiPanel("structure");
  }

  get classList() {
    return {
      "structure-panel-content-wrapper": true,
      "is-draggable": this.hasPermission,
      "drag-in-progress": uiStore.isCanvasElementBeingDragged,
      "force-grabbing-cursor": this.forceGrabbingCursor
    };
  }

  forceGrabbingCursor = false;

  firstDragMovement = true;

  draggedNodeId: false | number = false;

  lastSubmittedDrag: false | DragPayload = false;

  didMoveElement = false;

  unsubscribeFromActivateElementMutation: (() => void) | null = null;

  beforeCreate() {
    // @ts-ignore
    this.$options.components.ChildStructureNodes = require("./ChildStructureNodes.vue").default;
  }

  get activeElementId() {
    return uiStore.activeElementId;
  }

  mounted() {
    this.expandToShowActiveElementWatch();

    // We cannot use watch instead, because watch doesn't emit when the same value was written to state
    this.unsubscribeFromActivateElementMutation = this.$store.subscribe<
      MutationPayload & { payload: UIModule["activeElementId"] }
    >(({ type }) => {
      if (type === "ui/activateElementMutation") {
        this.expandToShowActiveElementWatch();
      }
    });
  }

  expandToShowActiveElementWatch() {
    if (this.activeElementId) {
      const expandedNodesWithActiveElementInside = expandNodesToShowActiveElement(
        documentStore.document.tree,
        this.activeElementId
      );

      if (expandedNodesWithActiveElementInside) {
        uiStore.setStructurePanelNodesExpandedState({
          ...uiStore.structurePanelNodesExpandedState,
          ...expandedNodesWithActiveElementInside
        });
      }

      // Wait until ChildStructureNodes will re-render
      this.$nextTick(() => {
        // Selecting an element will open the Controls panel and close Structure if they're on the same side
        // un-mounting it. But since this is on "nextTick" it'll still run. So we check if it still exists
        if (!this.$el || !this.panelHeader) {
          return;
        }

        const activeElementDomEl = this.$el.querySelector(
          `[data-element-id="${this.activeElementId}"] .structure-node-title-wrapper`
        );

        if (activeElementDomEl) {
          scrollToHtmlElementInsidePanel(
            this.$el,
            this.panelHeader.$el,
            activeElementDomEl
          );
        }
      });
    }
  }

  get rootElement() {
    return documentStore.document.tree.root;
  }

  get hasPermission() {
    return uiStore.hasFullAccess;
  }

  onContextMenu(e: MouseEvent) {
    if (!this.hasPermission) return;

    e.preventDefault();
    e.stopPropagation();

    const elementId =
      getNodeIdAtPointInStructurePanel(e.clientX, e.clientY) || undefined;

    if (elementId) {
      uiStore.activateElement(elementId);
    }

    uiStore.setOnStructurePanelRightClickMenuState({
      elementId,
      position: {
        x: e.clientX,
        y: e.clientY
      }
    });
  }

  easyEvent = new EasyEvent();

  dragStart(e: MouseEvent) {
    if (e.button !== 0) return; // Left-clicks only
    if (!this.hasPermission) return;

    const draggedNodeId = getNodeIdAtPointInStructurePanel(
      e.clientX,
      e.clientY
    );

    if (draggedNodeId) {
      uiStore.activateElement(draggedNodeId);

      this.draggedNodeId = draggedNodeId;

      this.easyEvent.on("mousemove", this.dragging);
      this.easyEvent.on("mouseup", this.dragEnd);
      this.forceGrabbingCursor = true;
      this.didMoveElement = false;

      breakdanceUndo.startTransaction();
    }
  }

  // throttledDragging = throttle(this.dragging, 80);

  async dragging(e: MouseEvent) {
    if (isCanvasEvent(e)) return;

    e.preventDefault();
    e.stopPropagation();

    if (!this.draggedNodeId) return;

    let dragPayload: null | DragPayload = null;

    if (this.firstDragMovement) {
      const nodeIndex = getNodeIndex(
        documentStore.document.tree,
        this.draggedNodeId
      );
      const nodeParent = getNodeParent(
        documentStore.document.tree,
        this.draggedNodeId
      );

      if (nodeIndex === false || nodeParent === false) return;

      uiStore.setCanvasElementDraggedId(this.draggedNodeId);

      dragPayload = {
        draggedElementId: this.draggedNodeId,
        droppedOnElementId: nodeParent.id,
        index: nodeIndex
      };

      this.firstDragMovement = false;
    } else {
      this.mouseDragStatus = getMouseDragStatus(
        e.clientX,
        e.clientY,
        documentStore.document.tree
      );

      this.ifMouseIsOverACollapsedNodeForLongEnoughThenWeShouldExpandIt(e);

      const newNodePosition = getNewNodePosition(
        this.mouseDragStatus,
        documentStore.document.tree,
        this.draggedNodeId
      );

      if (newNodePosition) {
        dragPayload = {
          draggedElementId: this.draggedNodeId,
          droppedOnElementId: newNodePosition.parentId,
          index: newNodePosition.index
        };
      }
    }

    if (dragPayload) {
      /*
      only move it inside if either:
       - the droppedOn element is already open
       - there are no children

      this prevents the ugly UX of an element disappearing when the droppedOn is collapsed

      (when there are no children, we do the move immediately and expand the node)
      */

      const droppedOnElement = findNode(
        documentStore.document.tree,
        dragPayload.droppedOnElementId
      );

      const isDroppedOnExpandedOrEmpty =
        structurePanelNodeIsExpanded(dragPayload.droppedOnElementId) ||
        droppedOnElement?.children.length === 0;

      if (isDroppedOnExpandedOrEmpty) {
        const elementMoved = await documentStore.moveElement({
          ...dragPayload,
          wrapWithTransaction: false
        });

        // if we moved it already, keep it
        this.didMoveElement = this.didMoveElement || elementMoved;

        uiStore.setStructurePanelNodesExpandedState({
          ...uiStore.structurePanelNodesExpandedState,
          [dragPayload.droppedOnElementId.toString()]: true
        });

        this.lastSubmittedDrag = dragPayload;
      }
    }
  }

  async dragEnd(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    this.easyEvent.removeAll();

    if (this.lastSubmittedDrag) {
      const elementMoved = await documentStore.moveElement({
        ...this.lastSubmittedDrag,
        wrapWithTransaction: false
      });

      // if we moved it already, keep it
      this.didMoveElement = this.didMoveElement || elementMoved;
    }

    if (this.draggedNodeId !== false && this.didMoveElement) {
      const draggedElement = findNode(
        documentStore.document.tree,
        this.draggedNodeId
      );
      breakdanceUndo.endTransaction(
        `Move ${getBuilderElementNameBySlug(draggedElement?.data.type)}`
      );
    } else {
      breakdanceUndo.cancelTransactionIfItIsStillInProgress();
    }

    uiStore.endCanvasElementDrag();

    this.lastSubmittedDrag = false;

    this.mouseDragStatus = {
      ...initialMouseDragStatus()
    };
    this.firstDragMovement = true;
    this.draggedNodeId = false;
    this.forceGrabbingCursor = false;
    this.didMoveElement = false;
  }

  ifMouseIsOverACollapsedNodeForLongEnoughThenWeShouldExpandIt(e: MouseEvent) {
    if (!this.mouseDragStatus.overNodeId) return;

    this.delayedExpandNode(this.mouseDragStatus.overNodeId, e);
  }

  delayedExpandNodeTimeout: NodeJS.Timeout | null = null;
  delayedExpandNode(nodeId: number, e: MouseEvent) {
    if (this.delayedExpandNodeTimeout) {
      clearTimeout(this.delayedExpandNodeTimeout);
    }

    this.delayedExpandNodeTimeout = setTimeout(() => {
      this.delayedExpandNodeTimeout = null;

      // check that the user didn't hover over some other element during the delay
      if (nodeId === this.mouseDragStatus.overNodeId) {
        const newStructurePanelNodesExpandedState = defensiveCopy(
          uiStore.structurePanelNodesExpandedState
        );

        newStructurePanelNodesExpandedState[nodeId] = true;

        uiStore.setStructurePanelNodesExpandedState(
          newStructurePanelNodesExpandedState
        );

        /*
        now that the node is expanded re-run the dragging method,
        because maybe now the node could be nested inside the open node
        */
        this.dragging(e);
      }
    }, 500);
  }

  get ROOT_ELEMENT_ID() {
    return ROOT_ELEMENT_ID;
  }

  collapseAllNodes() {
    uiStore.setStructurePanelNodesExpandedState(
      expandOrCollapseAllNodes(documentStore.document.tree, false)
    );
  }

  expandAllNodes() {
    uiStore.setStructurePanelNodesExpandedState(
      expandOrCollapseAllNodes(documentStore.document.tree, true)
    );
  }

  collapseAllNodesExcept(idOfNodeToKeepOpen: number) {
    const allNodesCollapsedState = expandOrCollapseAllNodes(
      documentStore.document.tree,
      false
    );

    const nodesToExpandState = expandNodesToShowActiveElement(
      documentStore.document.tree,
      idOfNodeToKeepOpen
    );

    uiStore.setStructurePanelNodesExpandedState({
      ...allNodesCollapsedState,
      ...nodesToExpandState
    });
  }

  toggleStage: 0 | 1 | 2 = 2;

  toggleNodes() {
    if (this.toggleStage === 0) {
      this.toggleStage = 1;
      this.collapseAllNodes();
    } else if (this.toggleStage === 1) {
      this.expandAllNodes();
      this.toggleStage = 2;
    } else if (uiStore.activeElementId) {
      const activeElementParent = getNodeParent(
        documentStore.document.tree,
        uiStore.activeElementId
      );

      if (activeElementParent && activeElementParent?.id === ROOT_ELEMENT_ID) {
        // The active node is at the root? Just collapse all, and skip stage 0
        this.collapseAllNodes();
        this.toggleStage = 1;

        return;
      }

      this.collapseAllNodesExcept(uiStore.activeElementId);
      this.toggleStage = 0;
    } else {
      // No active node? Just collapse all, and skip stage 0
      this.collapseAllNodes();
      this.toggleStage = 1;
    }
  }

  beforeDestroy() {
    this.unsubscribeFromActivateElementMutation &&
      this.unsubscribeFromActivateElementMutation();
  }

  toggleAdd() {
    toggleUiPanel("add");
  }
}
